/**
 * ScanButton + ScanModal — mini barcode/QR scanner voor Magazijn.
 *
 * Gebruikt `html5-qrcode` (geen externe SDK). Plaatsen:
 *  - extern W-nummer veld bij registratie nieuwe zending
 *  - W-nummer chip-input bij consolidatie-toevoegen
 *  - Magazijn-zoekbalk
 *
 * Fallback: als camera niet beschikbaar / geweigerd, toont nette NL-melding
 * en gebruiker kan handmatig invoeren. USB/BT-scanners blijven werken via
 * gewone Input-velden (typen + Enter).
 */
import React, { useEffect, useRef, useState } from "react";
import { ScanLine, X as XIcon, Camera, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";

import { Button } from "../../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";

// ScanButton — klein knopje voor inline gebruik naast inputs
export function ScanButton({ onResult, label = "Scan", title = "Barcode/QR scannen", testId = "scan-button", className = "", iconOnly = false }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testId}
        title={title}
        className={`inline-flex items-center justify-center gap-1.5 h-10 px-2.5 border border-gray-300 bg-white hover:bg-gray-50 text-[#082c50] text-xs font-bold uppercase tracking-wide ${className}`}
      >
        <ScanLine className="w-4 h-4" />
        {!iconOnly && <span>{label}</span>}
      </button>
      {open && (
        <ScanModal
          onClose={() => setOpen(false)}
          onResult={(val) => {
            onResult?.(val);
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

// ScanModal — open camera, lees barcode/QR, geef terug
export function ScanModal({ onClose, onResult }) {
  const containerRef = useRef(null);
  const scannerRef = useRef(null);
  const [status, setStatus] = useState("starting"); // starting | scanning | error | success
  const [scannedValue, setScannedValue] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let active = true;
    let Html5Qrcode;
    let Html5QrcodeScannerState;

    (async () => {
      try {
        // Dynamic import — voorkomt build-time afhankelijkheid bij SSR
        const mod = await import("html5-qrcode");
        Html5Qrcode = mod.Html5Qrcode;
        Html5QrcodeScannerState = mod.Html5QrcodeScannerState;
      } catch (e) {
        if (active) {
          setStatus("error");
          setErrorMsg("Scanner-module kon niet worden geladen.");
        }
        return;
      }
      if (!active || !containerRef.current) return;
      const id = "scan-modal-region";
      containerRef.current.id = id;
      let scanner;
      try {
        scanner = new Html5Qrcode(id, { verbose: false });
        await scanner.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 240, height: 180 },
            aspectRatio: 1.333,
          },
          (decodedText) => {
            if (!active) return;
            setScannedValue(decodedText.trim().toUpperCase());
            setStatus("success");
            // Stop direct na succesvolle scan — guarded
            const sc = scannerRef.current;
            if (sc) {
              try {
                const state = typeof sc.getState === "function" ? sc.getState() : null;
                const SCANNING = Html5QrcodeScannerState?.SCANNING ?? 2;
                const PAUSED = Html5QrcodeScannerState?.PAUSED ?? 3;
                if (state === SCANNING || state === PAUSED) {
                  sc.stop().catch(() => {});
                }
              } catch { /* state-check faalde, niet stoppen */ }
            }
          },
          () => { /* scan-frame zonder match — negeren */ },
        );
        // Pas hierna scannerRef binden — als start() faalt willen we
        // GEEN reference zodat cleanup hem niet probeert te stoppen.
        scannerRef.current = scanner;
        if (active) setStatus("scanning");
      } catch (err) {
        // start() faalde — instance bestaat wel maar is niet running.
        // BELANGRIJK: scannerRef.current NIET vullen → cleanup zal
        // geen stop() aanroepen → geen ongevangen promise-rejection.
        if (active) {
          setStatus("error");
          const name = err?.name || "";
          const msg = String(err?.message || err || "");
          const lower = msg.toLowerCase();
          if (name === "NotAllowedError" || lower.includes("permission") || lower.includes("notallowed")) {
            setErrorMsg("Camera-toegang geweigerd. Sta camera toe in je browserinstellingen of vul handmatig in.");
          } else if (name === "NotFoundError" || lower.includes("notfound") || lower.includes("nodevice")) {
            setErrorMsg("Geen camera gevonden. Vul handmatig in of sluit een scanner aan.");
          } else {
            setErrorMsg("Camera kan niet worden gestart. Vul handmatig in.");
          }
        }
      }
    })();

    return () => {
      active = false;
      const sc = scannerRef.current;
      scannerRef.current = null;
      if (!sc) return;
      // Guarded stop — voorkom 'Cannot stop, scanner is not running or paused'
      try {
        const state = typeof sc.getState === "function" ? sc.getState() : null;
        const SCANNING = Html5QrcodeScannerState?.SCANNING ?? 2;
        const PAUSED = Html5QrcodeScannerState?.PAUSED ?? 3;
        if (state === SCANNING || state === PAUSED) {
          sc.stop().catch(() => {}).finally(() => {
            try { sc.clear?.(); } catch { /* niet kritiek */ }
          });
        } else {
          try { sc.clear?.(); } catch { /* niet kritiek */ }
        }
      } catch {
        /* stop/clear faalde — zwaargestopte scanner is acceptabel */
      }
    };
  }, []);

  const restart = () => {
    setScannedValue("");
    setStatus("starting");
    setErrorMsg("");
    // Force re-mount door key prop wijziging zou ook werken; hier doe ik
    // herstart door window.location-reload van het scan-element niet wenselijk.
    // Simpelste: roep useEffect-cleanup + nieuwe start aan door close+open.
    onClose?.();
  };

  const confirm = () => {
    if (scannedValue) onResult?.(scannedValue);
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid="scan-modal">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-[#082c50] flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-[#0055FF]" /> Barcode of QR-code scannen
          </DialogTitle>
          <DialogDescription>
            Richt je camera op de barcode of QR-code. De waarde wordt automatisch overgenomen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {status === "error" ? (
            <div className="bg-red-50 border border-red-200 px-3 py-3 rounded-md flex items-start gap-2"
                 data-testid="scan-error">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-red-900">
                <div className="font-semibold">Scanner niet beschikbaar</div>
                <div className="text-xs mt-1">{errorMsg}</div>
              </div>
            </div>
          ) : (
            <>
              <div className="relative w-full aspect-square max-h-[300px] bg-black rounded-md overflow-hidden border border-gray-200">
                {/* Camera region — React mag hier GEEN children renderen
                    omdat html5-qrcode hier zelf <video>/<canvas> in stopt.
                    Dat zou anders een 'removeChild' conflict geven bij unmount. */}
                <div ref={containerRef} className="absolute inset-0" />
                {status === "starting" && (
                  <div className="absolute inset-0 flex items-center justify-center text-white text-xs gap-2 pointer-events-none">
                    <Camera className="w-4 h-4 animate-pulse" /> Camera starten…
                  </div>
                )}
              </div>
              {status === "scanning" && (
                <div className="text-xs text-gray-600 text-center" data-testid="scan-status-scanning">
                  Houd de code stil voor de camera…
                </div>
              )}
              {status === "success" && (
                <div className="bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-md flex items-center gap-2"
                     data-testid="scan-success">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <div className="text-sm">
                    <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-bold">Gescand</div>
                    <div className="font-mono font-bold text-emerald-900">{scannedValue}</div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-none text-xs h-9" data-testid="scan-cancel">
            <XIcon className="w-4 h-4 mr-1" /> Annuleren
          </Button>
          {status === "success" && (
            <>
              <Button variant="outline" onClick={restart} className="rounded-none text-xs h-9" data-testid="scan-rescan">
                <RefreshCw className="w-4 h-4 mr-1" /> Opnieuw scannen
              </Button>
              <Button onClick={confirm} className="rounded-none text-xs h-9 bg-[#0055FF] text-white" data-testid="scan-confirm">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Gebruik deze waarde
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ScanButton;
